import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import i18n from "../../i18n/i18n";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-regular-svg-icons";
import ModalBase from "../ModalBase";
import { FormCard, FormGrid } from "../FormCard";
import { DateRow, DetailRow, InputRow } from "../Rows";
import { formatSwedishTime } from "../../helpers/DateTime";
import LoadingSpinner from "../LoadingSpinner";
import Dropdown from "../Dropdown";
import { useAuth } from "../../providers/AuthContext";
import { get_RemoteManagement_get_diagnostics } from "../../api/Ocpp";
import { get_RemoteManagement_get_diagnostics as get_RemoteManagement_get_diagnostics_201 } from "../../api/Ocpp201";
import { isOcpp201 } from "../point/ocppPointHelpers";
import {
  downloadChargingPointDiagnostics,
  getChargingPointDiagnostics,
} from "../../api/ChargingPoint";

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;

const emptyDiagnosticsState = {
  startTime: "",
  stopTime: "",
  retries: "",
  retryInterval: "",
};

export const GetDiagnosticsContent = ({
  chargingPointPublicId,
  chargingPointName,
  ocppVersion,
  mode = "inline",
  disabled = false,
  supportsGetDiagnostics = true,
  unsupportedMessage,
}) => {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const unsupportedText = unsupportedMessage || t("diagnostics.unsupported");
  const [formState, setFormState] = useState(emptyDiagnosticsState);
  const [diagnostics, setDiagnostics] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerTable, setRowsPerTable] = useState(10);
  const pollingRef = useRef(null);
  const pollingStartedAtRef = useRef(null);
  const hasSeenUploadingRef = useRef(false);

  const isActive = true;

  const clearPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    pollingStartedAtRef.current = null;
    setIsPolling(false);
  };

  const loadDiagnostics = async ({ silent = false } = {}) => {
    if (!chargingPointPublicId) {
      return [];
    }

    if (!silent) {
      setIsLoadingList(true);
    }

    try {
      const accessToken = await getAccessToken();
      const list = await getChargingPointDiagnostics(chargingPointPublicId, accessToken);
      const normalizedList = Array.isArray(list) ? list : [];
      setDiagnostics(normalizedList);

      const hasUploading = normalizedList.some(
        (item) => (item?.status || "").toLowerCase() === "uploading"
      );
      const hasFreshUploading = normalizedList.some((item) =>
        isFreshUploading(item)
      );
      const hasTimedOut =
        pollingStartedAtRef.current &&
        Date.now() - pollingStartedAtRef.current >= MAX_POLL_DURATION_MS;

      if (hasUploading) {
        hasSeenUploadingRef.current = true;
      }

      if (hasTimedOut && !hasUploading) {
        clearPolling();
      }

      if (hasUploading && hasSeenUploadingRef.current && !hasFreshUploading) {
        clearPolling();
      }

      if (!hasUploading && hasSeenUploadingRef.current) {
        clearPolling();
      }

      return normalizedList;
    } catch (error) {
      if (!silent) {
        toast.error(error?.message || t("diagnostics.failedLoad"));
      }

      return [];
    } finally {
      if (!silent) {
        setIsLoadingList(false);
      }
    }
  };

  const startPolling = () => {
    if (pollingRef.current) {
      return;
    }

    pollingStartedAtRef.current = Date.now();
    setIsPolling(true);
    pollingRef.current = setInterval(() => {
      loadDiagnostics({ silent: true });
    }, POLL_INTERVAL_MS);
  };

  useEffect(() => {
    if (!isActive || !supportsGetDiagnostics) {
      clearPolling();
      return;
    }

    loadDiagnostics().then((list) => {
      const hasFreshUploading = list.some((item) => isFreshUploading(item));

      if (hasFreshUploading) {
        startPolling();
      }
    });

    return () => {
      clearPolling();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [isActive, chargingPointPublicId, supportsGetDiagnostics]);

  useEffect(() => {
    if (!isActive) {
      setFormState(emptyDiagnosticsState);
      setDiagnostics([]);
      hasSeenUploadingRef.current = false;
      setPage(1);
    }
  }, [isActive]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(diagnostics.length / rowsPerTable));
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [diagnostics.length, rowsPerTable, page]);

  const handleFormChange = (key, value) => {
    setFormState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const buildQueryParams = () => {
    const queryParams = {
      chargingPointPublicId,
    };

    if (formState.startTime) {
      queryParams.startTime = new Date(formState.startTime).toISOString();
    }

    if (formState.stopTime) {
      queryParams.stopTime = new Date(formState.stopTime).toISOString();
    }

    if (formState.retries !== "") {
      const retries = Number(formState.retries);
      if (!Number.isNaN(retries)) {
        queryParams.retries = retries;
      }
    }

    if (formState.retryInterval !== "") {
      const retryInterval = Number(formState.retryInterval);
      if (!Number.isNaN(retryInterval)) {
        queryParams.retryInterval = retryInterval;
      }
    }

    return queryParams;
  };

  const handleSubmit = async () => {
    if (!chargingPointPublicId) {
      toast.error(t("diagnostics.missingPointId"));
      return;
    }

    setIsSubmitting(true);

    try {
      const accessToken = await getAccessToken();
      const requestDiagnostics = isOcpp201(ocppVersion)
        ? get_RemoteManagement_get_diagnostics_201
        : get_RemoteManagement_get_diagnostics;
      await requestDiagnostics(accessToken, buildQueryParams());
      toast.success(t("diagnostics.requestSent"));
      hasSeenUploadingRef.current = false;
      startPolling();
      const list = await loadDiagnostics();
      const hasFreshUploading = list.some((item) => isFreshUploading(item));
      if (!hasFreshUploading && list.length > 0 && !list.some((item) => (item?.status || "").toLowerCase() === "uploading")) {
        clearPolling();
      }
    } catch (error) {
      toast.error(error?.message || t("diagnostics.failedRequest"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = async (item) => {
    try {
      const accessToken = await getAccessToken();
      const response = await downloadChargingPointDiagnostics(
        item.diagnosticsPublicId,
        accessToken
      );
      const url = window.URL.createObjectURL(response.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        getFileNameFromContentDisposition(response.contentDisposition) ||
        item.fileName ||
        `${item.diagnosticsPublicId}.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.message || t("diagnostics.failedDownload"));
    }
  };

  const requestContent = (
    <>
      <FormGrid className="gap-y-0">
        <DetailRow
          label={t("entity.chargingPoint")}
          value={chargingPointName || chargingPointPublicId}
        />
        <DateRow
          label={t("form.startTime")}
          value={formState.startTime}
          onChange={(value) => handleFormChange("startTime", value)}
        />
        <DateRow
          label={t("form.stopTime")}
          value={formState.stopTime}
          onChange={(value) => handleFormChange("stopTime", value)}
        />
        <InputRow
          label={t("form.retries")}
          value={formState.retries}
          onChange={(value) => handleFormChange("retries", value)}
          isNumber
        />
        <InputRow
          label={t("form.retryInterval")}
          value={formState.retryInterval}
          onChange={(value) => handleFormChange("retryInterval", value)}
          isNumber
        />
      </FormGrid>

      <div className="flex justify-end gap-3 pt-6">
        <button
        className="btn btn-primary"
         onClick={handleSubmit}
         disabled={isSubmitting || isPolling || disabled}
        >
          {isSubmitting || isPolling ? t("common.loading") : t("common.fetch")}
        </button>
      </div>

      {(isSubmitting || isPolling) && (
        <LoadingSpinner text={t("diagnostics.loading")} className="py-4" />
      )}
    </>
  );

  const unsupportedContent = (
    <FormGrid className="gap-y-0">
      <DetailRow
        label={t("common.status")}
        value={unsupportedText}
      />
    </FormGrid>
  );

  const totalRows = diagnostics.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerTable));
  const startIndex = totalRows === 0 ? 0 : (page - 1) * rowsPerTable + 1;
  const endIndex = Math.min(page * rowsPerTable, totalRows);
  const paginatedDiagnostics = diagnostics.slice(
    (page - 1) * rowsPerTable,
    page * rowsPerTable
  );

  const tableContent = (
    <div className="overflow-x-auto">
      <table className="table-default w-full">
        <thead>
          <tr>
            <th className="text-left">{t("common.file")}</th>
            <th className="text-left">{t("common.status")}</th>
            <th className="text-left">{t("common.createdAt")}</th>
            <th className="text-left">{t("common.start")}</th>
            <th className="text-left">{t("common.end")}</th>
            <th className="text-right">{t("common.action")}</th>
          </tr>
        </thead>
        <tbody>
          {paginatedDiagnostics.length === 0 && (
            <tr>
              <td className="text-left text-mute-500" colSpan={6}>
                {isLoadingList ? t("diagnostics.loading") : t("diagnostics.noneFound")}
              </td>
            </tr>
          )}

          {paginatedDiagnostics.map((item) => (
            <tr key={item.diagnosticsPublicId}>
              <td className="text-left font-medium">
                {item.fileName || item.diagnosticsPublicId}
              </td>
              <td className="text-left">{getDisplayStatus(item)}</td>
              <td className="text-left">{formatDateTime(item.createdDateTimeUtc)}</td>
              <td className="text-left">{formatDateTime(item.startDateTimeUtc)}</td>
              <td className="text-left">{formatDateTime(item.stopDateTimeUtc)}</td>
              <td className="text-right">
                {(item.status || "").toLowerCase() === "blobuploaded" && (
                  <button
                    className="btn-sm btn-outline-primary"
                    onClick={() => handleDownload(item)}
                    disabled={!item.diagnosticsPublicId}
                  >
                    {t("common.download")}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const paginationContent = totalRows > 0 && (
    <div className="flex gap-4 justify-end items-center text-black pt-4">
      <p>{t("pagination.rowsPerPage")}</p>
      <div className="w-32">
        <Dropdown
          className={"mb-1"}
          options={["10", "30", "50", "70", "100"]}
          onSelect={(rows) => {
            setPage(1);
            setRowsPerTable(parseInt(rows, 10));
          }}
          selectedOptions={[String(rowsPerTable)]}
        />
      </div>
      <p>{t("pagination.range", { start: startIndex, end: endIndex, total: totalRows })}</p>
      <FontAwesomeIcon
        className={`cursor-pointer ${page <= 1 ? "opacity-40" : ""}`}
        icon={faChevronLeft}
        onClick={() => {
          if (page > 1) {
            setPage(page - 1);
          }
        }}
      />
      <FontAwesomeIcon
        className={`cursor-pointer ${page >= totalPages ? "opacity-40" : ""}`}
        icon={faChevronRight}
        onClick={() => {
          if (page < totalPages) {
            setPage(page + 1);
          }
        }}
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      {!supportsGetDiagnostics ? (
        unsupportedContent
      ) : mode === "modal" ? (
        <>
          <FormCard
            title={t("diagnostics.request")}
            className="border border-mute-200 p-6"
            collapsible={true}
          >
            {requestContent}
          </FormCard>
          <FormCard
            title={t("diagnostics.files")}
            className="border border-mute-200 p-6"
          >
            {tableContent}
            {paginationContent}
          </FormCard>
        </>
      ) : (
        <>
          {requestContent}
          <div className="pt-2">
            <h3 className="text-lg font-semibold mb-4">{t("diagnostics.files")}</h3>
            {tableContent}
            {paginationContent}
          </div>
        </>
      )}
    </div>
  );
};

const GetDiagnosticsModal = ({
  isOpen = false,
  onClose,
  chargingPointPublicId,
  chargingPointName,
  ocppVersion,
  supportsGetDiagnostics = true,
}) => {
  const { t } = useTranslation();
  const handleClose = () => {
    onClose?.();
  };

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      className="max-h-[85vh] overflow-y-auto"
    >
      <div className="flex flex-col gap-5">
        <div>
          <h2 className="text-xl font-bold">{t("diagnostics.title")}</h2>
          <p className="text-sm text-mute-500 mt-1">
            {t("diagnostics.description")}
          </p>
        </div>

        <GetDiagnosticsContent
          mode="modal"
          chargingPointPublicId={chargingPointPublicId}
          chargingPointName={chargingPointName}
          ocppVersion={ocppVersion}
          supportsGetDiagnostics={supportsGetDiagnostics}
        />
      </div>
    </ModalBase>
  );
};

export default GetDiagnosticsModal;

function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  return formatSwedishTime(value, false);
}

function isFreshUploading(item) {
  if ((item?.status || "").toLowerCase() !== "uploading") {
    return false;
  }

  if (!item?.updatedDateTimeUtc) {
    return false;
  }

  const updatedAt = new Date(item.updatedDateTimeUtc);
  if (Number.isNaN(updatedAt.getTime())) {
    return false;
  }

  return Date.now() - updatedAt.getTime() < MAX_POLL_DURATION_MS;
}

function getDisplayStatus(item) {
  const status = item?.status || "-";

  if ((item?.status || "").toLowerCase() === "uploading" && !isFreshUploading(item)) {
    return i18n.t("diagnostics.uploadingTimeout");
  }

  return status;
}

function getFileNameFromContentDisposition(contentDisposition) {
  if (!contentDisposition) {
    return "";
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const asciiMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  return asciiMatch?.[1] || "";
}
